import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, departmentsAPI } from '../lib/api';
import api from '../lib/api';
import {
  Check, X, Search, Filter, ChevronDown, Eye, Settings,
  Users, BookOpen, BarChart2, Save, AlertCircle, Building, Plus, Trash2, Edit2, Linkedin
} from 'lucide-react';
import { Badge, Spinner, LoadingPage, PageHeader, Modal, Alert, StatCard, formatCurrency, formatDateTime, Tabs, Avatar } from '../components/shared';
import toast from 'react-hot-toast';

// ── USER MANAGEMENT ────────────────────────────────────────────────────────
export function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ role: '', status: 'pending', search: '', page: 1 });
  const [actionModal, setActionModal] = useState(null); // { user, action }
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [judgeProfileModal, setJudgeProfileModal] = useState(null); // { user }
  const [judgeProfileData, setJudgeProfileData] = useState({ affiliation: '', bio: '', linkedin_url: '', expertise: '', headshot: null });
  const [judgeProfileProcessing, setJudgeProfileProcessing] = useState(false);

  const handleJudgeProfileSave = async () => {
    if (!judgeProfileModal) return;
    setJudgeProfileProcessing(true);
    try {
      const formData = new FormData();
      if (judgeProfileData.affiliation) formData.append('affiliation', judgeProfileData.affiliation);
      if (judgeProfileData.bio) formData.append('bio', judgeProfileData.bio);
      if (judgeProfileData.linkedin_url) formData.append('linkedin_url', judgeProfileData.linkedin_url);
      if (judgeProfileData.expertise) formData.append('expertise', judgeProfileData.expertise);
      if (judgeProfileData.headshot) formData.append('headshot', judgeProfileData.headshot);
      await adminAPI.updateJudgeProfile(judgeProfileModal.user.id, formData);
      toast.success('Judge profile updated.');
      setJudgeProfileModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update profile.');
    } finally {
      setJudgeProfileProcessing(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: 20 };
      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const res = await adminAPI.getUsers(params);
      setUsers(res.data.users);
      setPagination(res.data.pagination);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAction = async () => {
    setProcessing(true);
    try {
      await adminAPI.updateUserStatus(actionModal.user.id, {
        status: actionModal.action,
        reason
      });
      toast.success(`User ${actionModal.action}`);
      setActionModal(null);
      setReason('');
      fetchUsers();
    } catch {
      toast.error('Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const tabDefs = [
    { id: 'pending', label: 'Pending Review' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: '', label: 'All Users' },
  ];

  return (
    <div>
      <PageHeader eyebrow="Admin" title="User Management" subtitle={`${pagination.total || 0} users total`} />

      <Tabs
        tabs={tabDefs}
        active={filters.status}
        onChange={s => setFilters(f => ({ ...f, status: s, page: 1 }))}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="form-input" style={{ paddingLeft: 38 }} placeholder="Search by name or email…" value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <select className="form-select" style={{ width: 'auto' }} value={filters.role}
          onChange={e => setFilters(f => ({ ...f, role: e.target.value, page: 1 }))}>
          <option value="">All Roles</option>
          <option value="student">Students</option>
          <option value="lecturer">Lecturers</option>
          <option value="alumni">Alumni</option>
          <option value="judge">Judges</option>
        </select>
      </div>

      {loading ? <LoadingPage /> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={`${u.first_name} ${u.last_name}`} size={32} />
                      <div>
                        <div style={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td><Badge status={u.role} /></td>
                  <td><Badge status={u.status} /></td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{formatDateTime(u.created_at)}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{u.last_login ? formatDateTime(u.last_login) : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {u.role === 'judge' && (
                        <button className="btn btn-sm btn-secondary" onClick={() => {
                          setJudgeProfileModal({ user: u });
                          setJudgeProfileData({ affiliation: '', bio: '', linkedin_url: '', expertise: '', headshot: null });
                        }}>
                          <Edit2 size={13} /> Profile
                        </button>
                      )}
                      {u.status === 'pending' && (
                        <>
                          <button className="btn btn-sm btn-teal" onClick={() => setActionModal({ user: u, action: 'approved' })}>
                            <Check size={13} /> Approve
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => setActionModal({ user: u, action: 'rejected' })}>
                            <X size={13} /> Reject
                          </button>
                        </>
                      )}
                      {u.status === 'approved' && (
                        <button className="btn btn-sm btn-danger" onClick={() => setActionModal({ user: u, action: 'suspended' })}>
                          Suspend
                        </button>
                      )}
                      {(u.status === 'suspended' || u.status === 'rejected') && (
                        <button className="btn btn-sm btn-secondary" onClick={() => setActionModal({ user: u, action: 'approved' })}>
                          Restore
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              No users found matching the current filter.
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          <button
            className="btn btn-secondary btn-sm"
            disabled={filters.page <= 1}
            onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
          >
            ← Prev
          </button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              className={`btn btn-sm ${p === filters.page ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilters(f => ({ ...f, page: p }))}
              style={{ minWidth: 36 }}
            >
              {p}
            </button>
          ))}
          <button
            className="btn btn-secondary btn-sm"
            disabled={filters.page >= pagination.pages}
            onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
          >
            Next →
          </button>
        </div>
      )}

      {/* Judge Profile Modal */}
      <Modal
        open={!!judgeProfileModal}
        onClose={() => setJudgeProfileModal(null)}
        title={`Edit Judge Profile — ${judgeProfileModal?.user.first_name} ${judgeProfileModal?.user.last_name}`}
      >
        {judgeProfileModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Affiliation</label>
              <input className="form-input" placeholder="e.g. EEE Department, OAU / Google"
                value={judgeProfileData.affiliation}
                onChange={e => setJudgeProfileData(d => ({ ...d, affiliation: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">LinkedIn URL</label>
              <input className="form-input" placeholder="https://linkedin.com/in/..."
                value={judgeProfileData.linkedin_url}
                onChange={e => setJudgeProfileData(d => ({ ...d, linkedin_url: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Expertise (comma-separated)</label>
              <input className="form-input" placeholder="e.g. Project Management, Telecoms, AI"
                value={judgeProfileData.expertise}
                onChange={e => setJudgeProfileData(d => ({ ...d, expertise: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Bio</label>
              <textarea className="form-textarea" style={{ minHeight: 120 }}
                placeholder="Professional background and expertise..."
                value={judgeProfileData.bio}
                onChange={e => setJudgeProfileData(d => ({ ...d, bio: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Headshot</label>
              <input type="file" accept="image/jpeg,image/png,image/jpg"
                style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}
                onChange={e => setJudgeProfileData(d => ({ ...d, headshot: e.target.files[0] || null }))} />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>JPG or PNG. Will appear on the public judges section.</div>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setJudgeProfileModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleJudgeProfileSave} disabled={judgeProfileProcessing}>
                {judgeProfileProcessing ? <Spinner size={16} /> : <><Save size={14} /> Save Profile</>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Action Modal */}
      <Modal
        open={!!actionModal}
        onClose={() => { setActionModal(null); setReason(''); }}
        title={`${actionModal?.action === 'approved' ? 'Approve' : actionModal?.action === 'rejected' ? 'Reject' : 'Suspend'} User`}
      >
        {actionModal && (
          <div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>
              You are about to <strong style={{ color: 'var(--text-primary)' }}>{actionModal.action}</strong> the account of{' '}
              <strong style={{ color: 'var(--gold)' }}>{actionModal.user.first_name} {actionModal.user.last_name}</strong>.
            </p>
            {actionModal.action !== 'approved' && (
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Reason (optional)</label>
                <textarea className="form-textarea" style={{ minHeight: 80 }} placeholder="Provide a reason…"
                  value={reason} onChange={e => setReason(e.target.value)} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { setActionModal(null); setReason(''); }}>Cancel</button>
              <button
                className={`btn ${actionModal.action === 'approved' ? 'btn-teal' : 'btn-danger'}`}
                onClick={handleAction}
                disabled={processing}
              >
                {processing ? <Spinner size={16} /> : `Confirm ${actionModal.action}`}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── TOPIC APPROVAL ─────────────────────────────────────────────────────────
export function AdminTopicsPage() {
  const [pendingTopics, setPendingTopics] = useState([]);
  const [deletionTopics, setDeletionTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewData, setReviewData] = useState({ action: 'approve', reason: '' });
  const [processing, setProcessing] = useState(false);
  const [deletionProcessing, setDeletionProcessing] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      adminAPI.getPendingTopics(),
      api.get('/admin/topics/deletion-requests'),
    ])
      .then(([pendingRes, deletionRes]) => {
        setPendingTopics(pendingRes.data);
        setDeletionTopics(deletionRes.data || []);
      })
      .catch(() => toast.error('Failed to load topics'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleReview = async () => {
    setProcessing(true);
    try {
      await adminAPI.approveTopic(reviewModal.id, reviewData);
      toast.success(`Topic ${reviewData.action}d`);
      setPendingTopics(prev => prev.filter(t => t.id !== reviewModal.id));
      setReviewModal(null);
    } catch {
      toast.error('Failed to process topic');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveDeletion = async (topicId) => {
    setDeletionProcessing(p => ({ ...p, [topicId]: 'deleting' }));
    try {
      await api.delete(`/admin/topics/${topicId}`);
      toast.success('Topic deleted.');
      setDeletionTopics(prev => prev.filter(t => t.id !== topicId));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete topic.');
    } finally {
      setDeletionProcessing(p => ({ ...p, [topicId]: null }));
    }
  };

  const handleRejectDeletion = async (topicId) => {
    setDeletionProcessing(p => ({ ...p, [topicId]: 'rejecting' }));
    try {
      await api.post(`/admin/topics/${topicId}/reject-deletion`);
      toast.success('Deletion request rejected.');
      setDeletionTopics(prev => prev.filter(t => t.id !== topicId));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject request.');
    } finally {
      setDeletionProcessing(p => ({ ...p, [topicId]: null }));
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Topic Management"
        subtitle={`${pendingTopics.length} pending approval · ${deletionTopics.length} deletion requests`} />

      {/* Deletion Requests */}
      {deletionTopics.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600, color: 'var(--error)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trash2 size={16} /> Deletion Requests ({deletionTopics.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {deletionTopics.map(topic => (
              <div key={topic.id} className="card" style={{ border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.03)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
                  <div style={{ flex: 1 }}>
                    <div className="eyebrow" style={{ marginBottom: 4 }}>{topic.domain}</div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 6 }}>{topic.title}</h3>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                      Submitted by {topic.requested_by_name} · {new Date(topic.deletion_requested_at).toLocaleDateString()}
                    </div>
                    <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--text-secondary)', borderLeft: '3px solid var(--error)' }}>
                      <strong style={{ color: 'var(--error)' }}>Reason:</strong> {topic.deletion_reason}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                    <button
                      className="btn btn-danger btn-sm"
                      disabled={!!deletionProcessing[topic.id]}
                      onClick={() => handleApproveDeletion(topic.id)}
                    >
                      {deletionProcessing[topic.id] === 'deleting' ? <Spinner size={13} /> : <><Trash2 size={13} /> Delete Topic</>}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      disabled={!!deletionProcessing[topic.id]}
                      onClick={() => handleRejectDeletion(topic.id)}
                    >
                      {deletionProcessing[topic.id] === 'rejecting' ? <Spinner size={13} /> : 'Reject Request'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Approval */}
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600, marginBottom: 14 }}>
        Pending Approval ({pendingTopics.length})
      </h2>

      {pendingTopics.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <Check size={40} style={{ color: 'var(--success)', margin: '0 auto 16px', opacity: 0.6 }} />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400, marginBottom: 8 }}>All clear!</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No topics awaiting approval.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {pendingTopics.map(topic => (
            <div key={topic.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>{topic.domain}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 500, marginBottom: 8 }}>{topic.title}</h3>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                    By {topic.lecturer_title} {topic.lecturer_name} · {topic.department}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{topic.description}</p>
                  {topic.required_skills?.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {topic.required_skills.map((s, i) => (
                        <span key={i} style={{ background: 'rgba(45,212,191,0.08)', color: 'var(--teal)', border: '1px solid rgba(45,212,191,0.15)', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-teal" onClick={() => { setReviewModal(topic); setReviewData({ action: 'approve', reason: '' }); }}>
                    <Check size={14} /> Approve
                  </button>
                  <button className="btn btn-danger" onClick={() => { setReviewModal(topic); setReviewData({ action: 'reject', reason: '' }); }}>
                    <X size={14} /> Request Revision
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!reviewModal} onClose={() => setReviewModal(null)}
        title={reviewData.action === 'approve' ? 'Approve Topic' : 'Request Revision'}>
        {reviewModal && (
          <div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              {reviewData.action === 'approve'
                ? `Approving "${reviewModal.title}" will make it live on the marketplace.`
                : `The lecturer will be notified to revise their topic.`}
            </p>
            {reviewData.action === 'reject' && (
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Reason for Revision *</label>
                <textarea className="form-textarea" placeholder="Explain what needs to be changed…"
                  value={reviewData.reason} onChange={e => setReviewData(d => ({ ...d, reason: e.target.value }))} required />
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setReviewModal(null)}>Cancel</button>
              <button className={`btn ${reviewData.action === 'approve' ? 'btn-teal' : 'btn-danger'}`}
                onClick={handleReview}
                disabled={processing || (reviewData.action === 'reject' && !reviewData.reason)}>
                {processing ? <Spinner size={16} /> : `Confirm ${reviewData.action === 'approve' ? 'Approval' : 'Revision Request'}`}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── PROGRAM CONFIGURATION ──────────────────────────────────────────────────
export function AdminConfigPage() {
  const [config, setConfig] = useState({});
  const [updates, setUpdates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusModal, setStatusModal] = useState(null);

  useEffect(() => {
    adminAPI.getConfig()
      .then(r => setConfig(r.data))
      .catch(() => toast.error('Failed to load configuration'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => setUpdates(u => ({ ...u, [key]: value }));

  const getValue = (key, fallback = '') =>
    updates[key] !== undefined ? updates[key] : (config[key]?.value ?? fallback);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateConfig(updates);
      toast.success('Configuration saved');
      setConfig(c => {
        const next = { ...c };
        Object.entries(updates).forEach(([k, v]) => { next[k] = { ...next[k], value: v }; });
        return next;
      });
      setUpdates({});
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusTransition = async (newStatus) => {
    setStatusModal(null);
    setSaving(true);
    try {
      await adminAPI.updateConfig({ season_status: newStatus });
      toast.success(`Season marked as ${newStatus}`);
      setConfig(c => ({ ...c, season_status: { ...c.season_status, value: newStatus } }));
    } catch {
      toast.error('Failed to update season status');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.keys(updates).length > 0;
  const currentStatus = getValue('season_status', 'active');

  const statusConfig = {
    draft:  { label: 'Draft',  color: 'var(--text-muted)',  next: 'active', nextLabel: 'Activate Season', nextClass: 'btn-teal',   warning: 'Activating will open the platform to students and alumni.' },
    active: { label: 'Active', color: 'var(--success)',     next: 'closed', nextLabel: 'Close Season',    nextClass: 'btn-danger', warning: 'Closing the season is permanent. Pledge renewal emails will become available. Are you sure?' },
    closed: { label: 'Closed', color: 'var(--text-muted)',  next: 'draft',  nextLabel: 'Start New Season', nextClass: 'btn-teal', warning: 'This starts the next season in draft. Update the season name and dates first.' },
  };
  const statusInfo = statusConfig[currentStatus] || statusConfig.active;

  const dateFields = [
    { key: 'registration_deadline',     label: 'Registration Deadline' },
    { key: 'topic_submission_deadline', label: 'Topic Submission Deadline' },
    { key: 'sponsorship_deadline',      label: 'Sponsorship Deadline' },
    { key: 'application_deadline',      label: 'Application Deadline' },
    { key: 'submission_deadline',       label: 'Project Submission Deadline' },
    { key: 'judging_start',             label: 'Judging Start Date' },
    { key: 'judging_end',               label: 'Judging End Date' },
    { key: 'results_date',              label: 'Results Announcement Date' },
  ];

  const nairaFields = [
    { key: 'prize_first',    label: '1st Place Prize' },
    { key: 'prize_second',   label: '2nd Place Prize' },
    { key: 'prize_third',    label: '3rd Place Prize' },
    { key: 'prize_lecturer', label: 'Supervising Lecturer Prize' },
  ];

  const tierFields = [
    { key: 'tier_silver_min',   label: 'Silver',   color: 'rgba(192,192,192,0.9)' },
    { key: 'tier_gold_min',     label: 'Gold',     color: 'var(--gold)' },
    { key: 'tier_platinum_min', label: 'Platinum', color: 'var(--teal)' },
  ];

  if (loading) return <LoadingPage />;

  return (
    <div>
      <PageHeader
        eyebrow="Admin"
        title="Season Configuration"
        subtitle="Control season identity, timeline, prizes, and platform settings"
        actions={
          <button className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? <Spinner size={16} /> : <><Save size={15} /> Save Changes</>}
          </button>
        }
      />

      {hasChanges && (
        <div style={{ marginBottom: 20 }}>
          <Alert type="warning">{Object.keys(updates).length} unsaved change(s). Click "Save Changes" to apply.</Alert>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Season Identity */}
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 20 }}>Season Identity</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Programme Name</label>
              <input className="form-input" type="text" value={getValue('program_name')}
                onChange={e => handleChange('program_name', e.target.value)}
                style={updates.program_name ? { borderColor: 'var(--gold)' } : {}} />
            </div>
            <div className="form-group">
              <label className="form-label">Season Name</label>
              <input className="form-input" type="text" value={getValue('program_season')}
                onChange={e => handleChange('program_season', e.target.value)}
                placeholder="e.g. Season II"
                style={updates.program_season ? { borderColor: 'var(--gold)' } : {}} />
            </div>
          </div>
        </div>

        {/* Season Status */}
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 20 }}>Season Status</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>Current Status:</span>
                <span style={{
                  padding: '3px 12px', borderRadius: 999, fontSize: '0.8rem',
                  fontFamily: 'var(--font-mono)', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  background: currentStatus === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)',
                  color: statusInfo.color,
                  border: `1px solid ${currentStatus === 'active' ? 'rgba(34,197,94,0.25)' : 'var(--border-card)'}`,
                }}>
                  {statusInfo.label}
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                {currentStatus === 'draft'  && 'Season is not yet visible to students or alumni.'}
                {currentStatus === 'active' && 'Season is live. Students can apply, alumni can sponsor.'}
                {currentStatus === 'closed' && 'Season has ended. Pledge renewal emails are now available.'}
              </div>
            </div>
            {statusInfo.next && (
              <button
                className={`btn ${statusInfo.nextClass}`}
                style={{ flexShrink: 0 }}
                onClick={() => setStatusModal({ next: statusInfo.next, nextLabel: statusInfo.nextLabel, warning: statusInfo.warning })}
                disabled={saving}
              >
                {statusInfo.nextLabel}
              </button>
            )}
          </div>
        </div>

        {/* Finale Details */}
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 20 }}>Finale Details</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Finale Date</label>
              <input className="form-input" type="text" value={getValue('finale_date', '30 July 2026')}
                onChange={e => handleChange('finale_date', e.target.value)}
                placeholder="e.g. 30 July 2026"
                style={updates.finale_date ? { borderColor: 'var(--gold)' } : {}} />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Used in emails and platform displays. Requires backend restart to update emails.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Finale Venue</label>
              <input className="form-input" type="text" value={getValue('finale_venue', 'African Centre of Excellence, OAU')}
                onChange={e => handleChange('finale_venue', e.target.value)}
                placeholder="e.g. African Centre of Excellence, OAU"
                style={updates.finale_venue ? { borderColor: 'var(--gold)' } : {}} />
            </div>
          </div>
        </div>

        {/* Registration & Results toggles */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div className="card">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 20 }}>Registration Status</h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 500 }}>Allow New Registrations</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>Enable or disable the registration form</div>
              </div>
              <button onClick={() => handleChange('registration_open', getValue('registration_open') === 'true' ? 'false' : 'true')}
                style={{ width: 52, height: 28, borderRadius: 14, background: getValue('registration_open') === 'true' ? 'var(--teal)' : 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                <span style={{ position: 'absolute', top: 3, left: getValue('registration_open') === 'true' ? 28 : 4, width: 22, height: 22, borderRadius: 11, background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
              </button>
            </div>
          </div>
          <div className="card">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 20 }}>Public Results</h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 500 }}>Publish Winners Publicly</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>Show finalists and winners on the public website</div>
              </div>
              <button onClick={() => handleChange('results_published', getValue('results_published') === 'true' ? 'false' : 'true')}
                style={{ width: 52, height: 28, borderRadius: 14, background: getValue('results_published') === 'true' ? 'var(--gold)' : 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                <span style={{ position: 'absolute', top: 3, left: getValue('results_published') === 'true' ? 28 : 4, width: 22, height: 22, borderRadius: 11, background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
              </button>
            </div>
          </div>
        </div>

        {/* Programme Timeline */}
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 20 }}>Programme Timeline</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {dateFields.map(field => (
              <div className="form-group" key={field.key}>
                <label className="form-label">{field.label}</label>
                <input className="form-input" type="date" value={getValue(field.key)}
                  onChange={e => handleChange(field.key, e.target.value)}
                  style={updates[field.key] ? { borderColor: 'var(--gold)' } : {}} />
              </div>
            ))}
          </div>
        </div>

        {/* Prize Amounts */}
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 4 }}>Prize Amounts</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>Amounts in Naira. Displayed on the platform and in the programme booklet.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {nairaFields.map(field => (
              <div className="form-group" key={field.key}>
                <label className="form-label">{field.label}</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gold)', fontWeight: 600, fontSize: '0.9rem', pointerEvents: 'none' }}>₦</span>
                  <input className="form-input" type="number" min="0" step="1000"
                    value={getValue(field.key, '0')}
                    onChange={e => handleChange(field.key, e.target.value)}
                    style={{ paddingLeft: 28, ...(updates[field.key] ? { borderColor: 'var(--gold)' } : {}) }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sponsorship Tiers */}
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 4 }}>Sponsorship Tiers</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>Minimum pledge amounts (Naira) that qualify a sponsor for each tier.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            {tierFields.map(field => (
              <div className="form-group" key={field.key}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: field.color, fontWeight: 600 }}>{field.label}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Minimum</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gold)', fontWeight: 600, fontSize: '0.9rem', pointerEvents: 'none' }}>₦</span>
                  <input className="form-input" type="number" min="0" step="1000"
                    value={getValue(field.key, '0')}
                    onChange={e => handleChange(field.key, e.target.value)}
                    style={{ paddingLeft: 28, ...(updates[field.key] ? { borderColor: 'var(--gold)' } : {}) }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Department Bank Accounts */}
        <DepartmentManager />

      </div>

      {/* Season Status Transition Modal */}
      <Modal open={!!statusModal} onClose={() => setStatusModal(null)} title={statusModal?.nextLabel}>
        {statusModal && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <Alert type="warning">{statusModal.warning}</Alert>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setStatusModal(null)}>Cancel</button>
              <button
                className={`btn ${statusModal.next === 'active' ? 'btn-teal' : 'btn-danger'}`}
                onClick={() => handleStatusTransition(statusModal.next)}
                disabled={saving}
              >
                {saving ? <Spinner size={16} /> : `Confirm — ${statusModal.nextLabel}`}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function DepartmentManager() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newDept, setNewDept] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    departmentsAPI.getAll()
      .then(r => setDepartments(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSaveBankDetails = async (dept) => {
    setSaving(true);
    try {
      await departmentsAPI.update(dept.id, {
        bank_name: dept.bank_name,
        account_number: dept.account_number,
        account_name: dept.account_name,
      });
      setEditing(null);
      load();
    } catch { } finally { setSaving(false); }
  };

  const handleAddDept = async () => {
    if (!newDept.trim()) return;
    try {
      await departmentsAPI.create({ name: newDept.trim() });
      setNewDept('');
      load();
    } catch { }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500 }}>Department Bank Accounts</h2>
      </div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
        Alumni sponsorship payments are wired directly to the department bank account. Configure each department's account before the platform goes live.
      </p>
      {loading ? <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {departments.map(dept => (
            <div key={dept.id} style={{ border: '1px solid var(--border-card)', borderRadius: 10, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editing === dept.id ? 16 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Building size={16} style={{ color: 'var(--teal)' }} />
                  <span style={{ fontWeight: 500, fontSize: '0.95rem' }}>{dept.name}</span>
                  {dept.account_number
                    ? <span style={{ fontSize: '0.7rem', background: 'rgba(34,197,94,0.12)', color: 'var(--success)', padding: '2px 8px', borderRadius: 999, fontFamily: 'var(--font-mono)' }}>Configured</span>
                    : <span style={{ fontSize: '0.7rem', background: 'rgba(245,158,11,0.12)', color: 'var(--warning)', padding: '2px 8px', borderRadius: 999, fontFamily: 'var(--font-mono)' }}>Not configured</span>
                  }
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(editing === dept.id ? null : dept.id)}>
                  {editing === dept.id ? 'Cancel' : dept.account_number ? 'Edit' : 'Add Account'}
                </button>
              </div>
              {editing === dept.id && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Bank Name</label>
                    <input className="form-input" placeholder="e.g. First Bank" value={dept.bank_name || ''}
                      onChange={e => setDepartments(ds => ds.map(d => d.id === dept.id ? { ...d, bank_name: e.target.value } : d))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Account Number</label>
                    <input className="form-input" placeholder="e.g. 0123456789" value={dept.account_number || ''}
                      onChange={e => setDepartments(ds => ds.map(d => d.id === dept.id ? { ...d, account_number: e.target.value } : d))} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Account Name</label>
                    <input className="form-input" placeholder="e.g. OAU EEE Department" value={dept.account_name || ''}
                      onChange={e => setDepartments(ds => ds.map(d => d.id === dept.id ? { ...d, account_name: e.target.value } : d))} />
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary btn-sm" onClick={() => handleSaveBankDetails(dept)} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Account Details'}
                    </button>
                  </div>
                </div>
              )}
              {editing !== dept.id && dept.account_number && (
                <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', gap: 24 }}>
                  <span>{dept.bank_name}</span>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{dept.account_number}</span>
                  <span>{dept.account_name}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', paddingTop: 16, borderTop: '1px solid var(--border-card)' }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label className="form-label">Add New Department</label>
          <input className="form-input" placeholder="e.g. Mechanical Engineering" value={newDept}
            onChange={e => setNewDept(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddDept()} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleAddDept} style={{ marginBottom: 0 }}>
          <Plus size={14} /> Add
        </button>
      </div>
    </div>
  );
}

// ── RENEWAL MANAGEMENT ─────────────────────────────────────────────────────
export function AdminRenewalPage() {
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const { renewalAPI } = require('../lib/api');

  useEffect(() => {
    renewalAPI.getIntents()
      .then(r => setIntents(r.data))
      .catch(() => toast.error('Failed to load renewal intents'))
      .finally(() => setLoading(false));
  }, []);

  const handleSend = async () => {
    if (!window.confirm('Send renewal invitation emails to all Season II sponsors? This cannot be undone.')) return;
    setSending(true);
    try {
      const r = await renewalAPI.sendEmails();
      setSent(r.data);
      toast.success(r.data.message);
      renewalAPI.getIntents().then(r2 => setIntents(r2.data));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const confirmed = intents.filter(i => i.confirmed);
  const pending   = intents.filter(i => !i.confirmed);
  const totalIndicated = confirmed.reduce((s, i) => s + parseFloat(i.indicative_amount || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="Admin"
        title="Season Renewal"
        subtitle="Invite Season II sponsors to return for Season III"
        actions={
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? <Spinner size={16} /> : '📧 Send Renewal Invitations'}
          </button>
        }
      />

      {sent && (
        <div style={{ marginBottom: 20 }}>
          <Alert type="success">{sent.message}</Alert>
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 32 }}>
        <div className="card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Invitations Sent</div>
          <div style={{ fontSize: '1.8rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--teal)' }}>{intents.length}</div>
        </div>
        <div className="card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Confirmed Intent</div>
          <div style={{ fontSize: '1.8rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--success)' }}>{confirmed.length}</div>
        </div>
        <div className="card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Indicated Funding</div>
          <div style={{ fontSize: '1.8rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--gold)' }}>
            {totalIndicated > 0 ? `₦${Number(totalIndicated).toLocaleString()}` : '—'}
          </div>
        </div>
      </div>

      {loading ? <div style={{ color: 'var(--text-muted)' }}>Loading…</div> : (
        <>
          {confirmed.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 500, marginBottom: 16, color: 'var(--success)' }}>
                ✓ Confirmed ({confirmed.length})
              </h3>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead><tr><th>Sponsor</th><th>Company</th><th>Previous Total</th><th>Indicated for S3</th><th>Confirmed</th></tr></thead>
                  <tbody>
                    {confirmed.map(i => (
                      <tr key={i.id}>
                        <td><div style={{ fontWeight: 500 }}>{i.first_name} {i.last_name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{i.email}</div></td>
                        <td style={{ color: 'var(--text-secondary)' }}>{i.company || '—'}</td>
                        <td style={{ color: 'var(--gold)' }}>{i.prev_total ? `₦${Number(i.prev_total).toLocaleString()}` : '—'}</td>
                        <td style={{ color: 'var(--teal)', fontWeight: 500 }}>{i.indicative_amount ? `₦${Number(i.indicative_amount).toLocaleString()}` : 'Not specified'}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{i.confirmed_at ? new Date(i.confirmed_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {pending.length > 0 && (
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 500, marginBottom: 16, color: 'var(--text-muted)' }}>
                Awaiting Response ({pending.length})
              </h3>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead><tr><th>Sponsor</th><th>Company</th><th>Previous Total</th><th>Email Sent</th></tr></thead>
                  <tbody>
                    {pending.map(i => (
                      <tr key={i.id}>
                        <td><div style={{ fontWeight: 500 }}>{i.first_name} {i.last_name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{i.email}</div></td>
                        <td style={{ color: 'var(--text-secondary)' }}>{i.company || '—'}</td>
                        <td style={{ color: 'var(--gold)' }}>{i.prev_total ? `₦${Number(i.prev_total).toLocaleString()}` : '—'}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{i.email_sent_at ? new Date(i.email_sent_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {intents.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--text-muted)' }}>
              No renewal invitations sent yet. Click "Send Renewal Invitations" to begin.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── RECEIPT VERIFICATION ───────────────────────────────────────────────────
export function AdminReceiptsPage() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [note, setNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = () => {
    setLoading(true);
    adminAPI.getPendingReceipts()
      .then(r => setReceipts(r.data))
      .catch(() => toast.error('Failed to load receipts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAction = async (action) => {
    if (action === 'rejected' && !note.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await adminAPI.verifyReceipt(modal.id, { action, note });
      toast.success(`Receipt ${action}`);
      setModal(null);
      setNote('');
      load();
    } catch {
      toast.error('Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const totalPending = receipts.length;

  return (
    <div>
      <PageHeader
        eyebrow="Admin"
        title="Receipt Verification"
        subtitle={`${totalPending} receipt${totalPending !== 1 ? 's' : ''} awaiting review`}
      />

      {loading ? <LoadingPage /> : receipts.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <Check size={40} style={{ color: 'var(--success)', margin: '0 auto 16px', opacity: 0.6 }} />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400, marginBottom: 8 }}>All clear!</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No receipts awaiting verification.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {receipts.map(r => (
            <div key={r.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontWeight: 500, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                      {r.anonymous ? 'Anonymous Donor' : `${r.first_name} ${r.last_name}`}
                    </span>
                    <span style={{
                      fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, fontFamily: 'var(--font-mono)',
                      background: r.payment_status === 'rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                      color: r.payment_status === 'rejected' ? 'var(--error)' : 'var(--warning)',
                    }}>
                      {r.payment_status === 'rejected' ? 'Needs resubmission' : 'Awaiting review'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 4 }}>{r.topic_title}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', gap: 16 }}>
                    <span style={{ color: 'var(--gold)', fontWeight: 600 }}>₦{Number(r.amount).toLocaleString()}</span>
                    {r.department && <span>{r.department}</span>}
                    {!r.anonymous && <span>{r.email}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {r.receipt_url && (
                    <a href={r.receipt_url} target="_blank" rel="noreferrer"
                      className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem' }}>
                      View Receipt
                    </a>
                  )}
                  <button className="btn btn-sm btn-teal" onClick={() => { setModal(r); setNote(''); }}>
                    <Check size={13} /> Review
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!modal} onClose={() => { setModal(null); setNote(''); }} title="Review Payment Receipt">
        {modal && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{modal.first_name} {modal.last_name}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 4 }}>{modal.topic_title}</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--gold)', fontWeight: 600 }}>₦{Number(modal.amount).toLocaleString()}</div>
            </div>

            {modal.receipt_url && (
              <div style={{ marginBottom: 20 }}>
                <a href={modal.receipt_url} target="_blank" rel="noreferrer"
                  className="btn btn-ghost btn-sm" style={{ fontSize: '0.8rem' }}>
                  Open Receipt in New Tab
                </a>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Rejection reason (required only if rejecting)</label>
              <textarea className="form-textarea" style={{ minHeight: 80 }}
                placeholder="e.g. Amount on receipt does not match pledge amount…"
                value={note} onChange={e => setNote(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { setModal(null); setNote(''); }}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleAction('rejected')} disabled={processing}>
                {processing ? <Spinner size={16} /> : <><X size={14} /> Reject</>}
              </button>
              <button className="btn btn-teal" onClick={() => handleAction('verified')} disabled={processing}>
                {processing ? <Spinner size={16} /> : <><Check size={14} /> Verify Payment</>}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
